import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  BarChart3, Telescope, Sparkles, Loader2, AlertTriangle, CheckCircle2,
  CircleAlert, XCircle, BookOpen, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * WaveDDialog — Wave-D analyzer hub.
 *
 * Three tabs, each backed by an async background-polling job:
 *   • Statistical Reporting Checker — 10-item checklist + per-item fix suggestions
 *   • Research Gap Detector — surfaces topics in the evidence library not covered in the chapter
 *   • Novelty Checker — scores each substantive claim 0–10 vs. the chapter's library
 *
 * UX: when the user opens the dialog we DO NOT auto-run anything. They pick a
 * tab, click "Run", we kick off a background job and poll every 3 s.
 */
export default function WaveDDialog({
  open,
  onOpenChange,
  userId,
  projectId,
  chapter,
  language = 'en',
  initialTab = 'stats',
}) {
  const [tab, setTab] = useState(initialTab);
  // per-tab independent state — three concurrent jobs possible
  const [jobs, setJobs] = useState({ stats: null, gap: null, novelty: null });
  const pollersRef = useRef({ stats: null, gap: null, novelty: null });

  useEffect(() => { if (open) setTab(initialTab || 'stats'); }, [open, initialTab]);

  // Stop all polling when the dialog closes
  useEffect(() => {
    if (!open) {
      Object.values(pollersRef.current).forEach((p) => p && clearInterval(p));
      pollersRef.current = { stats: null, gap: null, novelty: null };
    }
    return () => {
      Object.values(pollersRef.current).forEach((p) => p && clearInterval(p));
    };
  }, [open]);

  const runJob = useCallback(async (kind) => {
    if (!chapter?.id || !projectId) {
      toast.error('Pick a chapter first.');
      return;
    }
    const apiKind = kind === 'stats' ? 'statistical-check'
      : kind === 'gap' ? 'gap-detect' : 'novelty-check';
    setJobs((s) => ({ ...s, [kind]: { status: 'queued', progress: 'Starting…', result: null } }));
    try {
      const { job_id } = await thesisApi.startWaveDJob(userId, apiKind, {
        project_id: projectId,
        chapter_id: chapter.id,
        n_chunks: kind === 'novelty' ? 24 : 18,
        retrieval_mode: 'hybrid',
        language,
      });
      // Begin polling every 3 s. Stop after 5 min just in case.
      const started = Date.now();
      if (pollersRef.current[kind]) clearInterval(pollersRef.current[kind]);
      pollersRef.current[kind] = setInterval(async () => {
        if (Date.now() - started > 5 * 60 * 1000) {
          clearInterval(pollersRef.current[kind]);
          setJobs((s) => ({ ...s, [kind]: { ...s[kind], status: 'failed', error: 'Timed out after 5 minutes' } }));
          return;
        }
        try {
          const j = await thesisApi.pollWaveDJob(userId, job_id);
          setJobs((s) => ({ ...s, [kind]: j }));
          if (j.status === 'ready' || j.status === 'failed') {
            clearInterval(pollersRef.current[kind]);
            pollersRef.current[kind] = null;
          }
        } catch {
          /* network blip — let next tick retry */
        }
      }, 3000);
    } catch (e) {
      toast.error(`Could not start ${kind}: ${e?.response?.data?.detail || e.message}`);
      setJobs((s) => ({ ...s, [kind]: { status: 'failed', error: e.message } }));
    }
  }, [chapter?.id, projectId, userId, language]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="wave-d-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Advanced Analyzers (Wave D)
          </DialogTitle>
          <DialogDescription>
            Chapter: <strong>{chapter?.title || '(none selected)'}</strong>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="stats" data-testid="wave-d-tab-stats">
              <BarChart3 className="w-4 h-4 mr-1.5" />Statistical Reporting
            </TabsTrigger>
            <TabsTrigger value="gap" data-testid="wave-d-tab-gap">
              <Telescope className="w-4 h-4 mr-1.5" />Research Gaps
            </TabsTrigger>
            <TabsTrigger value="novelty" data-testid="wave-d-tab-novelty">
              <Sparkles className="w-4 h-4 mr-1.5" />Novelty Check
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats" className="mt-4">
            <AnalyzerPane
              kind="stats" job={jobs.stats} onRun={() => runJob('stats')}
              emptyHint="Audits the chapter's Results section against 10 statistical-reporting items (sample size, 95% CI, multiple-testing correction, missing data, adverse events, etc.). Returns a checklist + per-item fix suggestions."
            >
              {jobs.stats?.result && <StatsResult result={jobs.stats.result} />}
            </AnalyzerPane>
          </TabsContent>

          <TabsContent value="gap" className="mt-4">
            <AnalyzerPane
              kind="gap" job={jobs.gap} onRun={() => runJob('gap')}
              emptyHint="Finds research questions / methods / outcomes that papers in the chapter's library DO address but the chapter does NOT discuss. Returns prioritised gaps with copy-pasteable draft paragraphs."
            >
              {jobs.gap?.result && <GapResult result={jobs.gap.result} />}
            </AnalyzerPane>
          </TabsContent>

          <TabsContent value="novelty" className="mt-4">
            <AnalyzerPane
              kind="novelty" job={jobs.novelty} onRun={() => runJob('novelty')}
              emptyHint="Scores each substantive claim in the chapter 0–10 for true novelty vs. the evidence pack. Flags claims that are already shown, restated, or contradicted by prior literature."
            >
              {jobs.novelty?.result && <NoveltyResult result={jobs.novelty.result} />}
            </AnalyzerPane>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ─── Shared layout for the per-tab body ────────────────────────────────────
function AnalyzerPane({ kind, job, onRun, emptyHint, children }) {
  const status = job?.status;
  const showRun = !status || status === 'failed';
  return (
    <div className="space-y-3">
      {!status && (
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{emptyHint}</p>
      )}

      {(status === 'queued' || status === 'running') && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-4 py-3 flex items-start gap-3">
          <Loader2 className="h-5 w-5 text-amber-600 animate-spin shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <p className="font-medium">Analysing chapter…</p>
            <p className="text-xs opacity-80 mt-0.5">{job.progress || 'Working in background.'}</p>
          </div>
        </div>
      )}

      {status === 'failed' && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-4 py-3 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-900 dark:text-red-200">
            <p className="font-medium">Analysis failed</p>
            <p className="text-xs opacity-80 mt-0.5">{job.error || 'Unknown error'}</p>
          </div>
        </div>
      )}

      {children}

      {showRun && (
        <div className="flex justify-end">
          <Button onClick={onRun} className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid={`wave-d-run-${kind}`}>
            <Sparkles className="w-4 h-4 mr-2" />
            {status === 'failed' ? 'Retry analysis' : 'Run analysis'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Result renderers ──────────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (status === 'ok')      return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === 'weak')    return <CircleAlert className="h-4 w-4 text-amber-600" />;
  return <XCircle className="h-4 w-4 text-red-600" />;
}

function StatsResult({ result }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-900 px-4 py-3">
        <div className="flex items-baseline gap-3">
          <div className="text-3xl font-bold text-violet-700 dark:text-violet-300">
            {result.overall_score}<span className="text-base text-slate-500">/100</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed flex-1">
            {result.summary}
          </p>
        </div>
      </div>
      <ol className="space-y-2">
        {(result.items || []).map((it) => (
          <li key={it.id} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2">
            <div className="flex items-start gap-2">
              <StatusBadge status={it.status} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{it.label}</p>
                {it.evidence_quote && (
                  <blockquote className="text-xs text-slate-600 dark:text-slate-400 italic border-l-2 border-slate-300 pl-2 mt-1">
                    "{it.evidence_quote}"
                  </blockquote>
                )}
                {it.suggestion && (
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    💡 {it.suggestion}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function GapResult({ result }) {
  const [open, setOpen] = useState(new Set(['g1']));
  const toggle = (id) => {
    const next = new Set(open);
    next.has(id) ? next.delete(id) : next.add(id);
    setOpen(next);
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-900 px-4 py-3">
        <div className="flex items-baseline gap-3">
          <div className="text-3xl font-bold text-violet-700 dark:text-violet-300">
            {result.coverage_score}<span className="text-base text-slate-500">/100</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed flex-1">
            <strong>Library coverage.</strong> {result.summary}
          </p>
        </div>
      </div>
      {(result.gaps || []).length === 0 && (
        <p className="text-sm text-slate-500 italic">No significant gaps detected — your chapter covers the library well.</p>
      )}
      <ul className="space-y-2">
        {(result.gaps || []).map((g) => (
          <li key={g.id} className={`rounded-md border bg-white dark:bg-slate-900 px-3 py-2 ${
            g.priority === 'high' ? 'border-red-300 dark:border-red-900'
              : g.priority === 'medium' ? 'border-amber-300 dark:border-amber-900'
              : 'border-slate-200 dark:border-slate-700'
          }`}>
            <button type="button" className="w-full text-left flex items-start gap-2"
              onClick={() => toggle(g.id)}>
              {open.has(g.id) ? <ChevronDown className="h-4 w-4 mt-0.5" /> : <ChevronRight className="h-4 w-4 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{g.title}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                    g.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                      : g.priority === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }`}>{g.priority}</span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{g.explanation}</p>
              </div>
            </button>
            {open.has(g.id) && (
              <div className="mt-2 pl-6 space-y-2">
                {g.suggested_paragraph && (
                  <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase text-blue-700 dark:text-blue-300 mb-1">
                      Draft paragraph (copy & paste)
                    </p>
                    <p className="text-xs text-slate-700 dark:text-slate-200 italic leading-relaxed">
                      {g.suggested_paragraph}
                    </p>
                  </div>
                )}
                {(g.supporting_paper_indices || []).length > 0 && (
                  <p className="text-[11px] text-slate-500 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    Supporting evidence: papers {g.supporting_paper_indices.map((i) => `[${i}]`).join(' ')}
                  </p>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NoveltyResult({ result }) {
  const scoreColor = (s) => s >= 8 ? 'text-emerald-600 dark:text-emerald-400'
    : s >= 5 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-900 px-4 py-3">
        <div className="flex items-baseline gap-3">
          <div className={`text-3xl font-bold ${scoreColor(result.overall_novelty)}`}>
            {result.overall_novelty}<span className="text-base text-slate-500">/10</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed flex-1">
            <strong>Overall novelty.</strong> {result.summary}
          </p>
        </div>
      </div>
      {(result.claims || []).length === 0 && (
        <p className="text-sm text-slate-500 italic">No substantive claims detected. Add original-contribution statements to your Discussion for the novelty checker to grade.</p>
      )}
      <ul className="space-y-2">
        {(result.claims || []).map((c) => (
          <li key={c.id} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2">
            <div className="flex items-start gap-3">
              <div className={`text-2xl font-bold ${scoreColor(c.novelty_score)} shrink-0`}>
                {c.novelty_score}
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                  c.verdict === 'novel' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : c.verdict === 'incremental' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                    : c.verdict === 'restated' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                }`}>{c.verdict}</span>
                <blockquote className="text-xs text-slate-700 dark:text-slate-300 italic border-l-2 border-slate-300 pl-2 mt-2">
                  "{c.claim_quote}"
                </blockquote>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">{c.rationale}</p>
                {(c.closest_paper_indices || []).length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    Closest: papers {c.closest_paper_indices.map((i) => `[${i}]`).join(' ')}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Re-export the small helpers for the warning icon used elsewhere
export { AlertTriangle };
